<template>
  <div class="practice-tool-result">
    <div class="animate__animated animate__tada mb-6">
      <h1 class="text-center" style="color: #05ab33; text-shadow: 2px 2px 3px #8d8e8e; font-size:2.5rem !important; font-weight: bold;">プラクティスツール 提出済み一覧</h1>
    </div>
    <p>
      <strong>該当の行を押下することで選択したプラクティスが開き、詳細を閲覧することができます。（反応しない場合は行の一番右のリンクを押下して下さい。）</strong>
      <br>※一度提出したプラクティスは編集・削除できません。
      <br>新しくプラクティスを開始する場合はメニューから「プラクティスツール」を押下してください。
    </p>
    <div v-if="!loading">
      <div>
        <div class="margin-select">
          <span>期間：&nbsp;</span>
          <el-select v-model="pickDate" placeholder="期間">
            <el-option
              v-for="item in optionsDate"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>

          <span class="ml-4">＋資金管理：&nbsp;</span>
          <el-select v-model="fundsManagement" placeholder="＋資金管理">
            <el-option
              v-for="item in optionsFunds"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
        </div>
        <div class="mb-6">
          <LineChart :chartData="lineChartData" :options="lineChartOptions" />
        </div>
      </div>
      <a :href="`${apiUrl}/practice/download-csv`">
        <el-button type="primary" plain>一覧ダウンロード（CSV）</el-button>
      </a>
      <div v-if="practices.length">
        <table class="table table-borderless table-striped table-hover">
          <thead>
            <tr>
              <th class="text-center">提出日付</th>
              <th class="text-center">銘柄と足種</th>
              <th class="text-center">期間</th>
              <th class="text-center">＋資金管理</th>
              <th class="text-center">取引回数</th>
              <th class="text-center">勝率％</th>
              <th class="text-center">リスクリワード</th>
              <th class="text-center">詳細リンク</th>
              <th class="text-center">評価</th>
              <th class="text-center">講師コメント</th>
            </tr>
          </thead>
          <tbody class="text-center">
            <template v-for="item in pagedList" :key="item.id">
              <tr @click="linkPractice(item.id)" class="cursor-pointer">
                <!-- 保存の都度、日付を差し替えているので提出日扱い -->
                <td>{{ dayjs(item.created_at).format('YYYY-MM-DD') }}</td>
                <td>{{ item.brand_name }} {{ item.chart }}</td>
                <td>{{ getDate(item) }}</td>
                <template v-if="item.funds_management"><td>◯</td></template>
                <template v-else><td></td></template>
                <td>{{ item.bodys ? item.bodys.length : 0 }}</td>
                <td>{{ item.win_rate }}</td>
                <td>{{ item.risk_reword_ratio }}</td>
                <td><a :href="getLink(item.id)" target="_blank">リンク</a></td>
                <template v-if="item.evaluation">
                  <td><img :src="geImage(config.evaluations[1].image)" width="120" height="60" :alt="config.evaluations[item.evaluation].name"></td>
                </template>
                <template v-else><td></td></template>
                <template v-if="item.instructor_comment"><td>◯</td></template>
                <template v-else><td></td></template>
              </tr>
            </template>
          </tbody>
        </table>
        <div>
          <base-pagination v-model:pagedList="pagedList" :originalList="practices" :pageSize="pageSize" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { axios } from '@/plugins/axios'
import { dayjs } from '@/plugins/dayjs'
import { evaluations } from './PracticeTool.vue'
import BasePagination from '@/components/BasePagination.vue'
import { ElLoading } from 'element-plus'

import { Chart, registerables } from 'chart.js'
import { LineChart } from 'vue-chart-3'
Chart.register(...registerables)

export default defineComponent({
  components: { BasePagination, LineChart },
  setup() {
    const route = useRoute()

    const lineChartData = ref({
      labels: '',
      datasets: [
        {
          label: '累計損益',
          backgroundColor: 'rgb(248, 121, 121)',
          pointBackgroundColor: 'rgb(255, 255, 255)',
          borderWidth: 1,
          pointBorderColor: 'rgb(36, 158, 191)',
          data: [],
          fill: true,
          tension: 0.2
        }
      ]
    })
    const lineChartOptions = ref({
      scales: {
        yAxis: {
          beginAtZero: true,
          ticks: {
            callback: function(label, index, labels) {
              return label.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            }
          }
        },
        xAxis: {
          beginAtZero: true,
        }
      },
      tooltips: {
        callbacks: {
          label: function(tooltipItem, data){
            return data.datasets[tooltipItem.datasetIndex].label + '：' + tooltipItem.yLabel.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          }
        }
      }
    })

    const pageSize = ref(10)
    const pagedList = ref([])

    const loading = ref(false)
    const optionsDate = ref([
      { label: '全期間', value: 1 },
      { label: '月', value: 2 },
      { label: '半年', value: 3 },
      { label: '年', value: 4 },
    ])
    const pickDate = ref(1)
    const optionsFunds = ref([
      { label: 'すべて', value: 2 },
      { label: '資金管理有り', value: 1 },
      { label: '資金管理無し', value: 0 },
    ])
    const fundsManagement = ref(2)
    const practices = ref([])
    const config = ref({
      evaluations: evaluations // 評価config
    })

    const fetchChartData = async (funds, pickDate) => {
      loading.value = true
      const loadingInstance = ElLoading.service()
      const query = Object.assign({}, route.query)
      let path = '/practice-tool/fetch-practice-chart-data'
      if (Object.keys(query).length) {
        if (query['page'] && query['uid'])
        {
          path = path + '?page=' + query['page'] + '&role=admin&uid=' + query['uid']
        }
        else if (query['page'])
        {
          path = path + '?page=' + query['page']
        }
        else if (query['uid']) {
          path = path + '?role=admin&uid=' + query['uid']
        }
      }

      const sdata = {
        pick_date: pickDate,
        funds: funds,
      }

      await axios
        .post(path, sdata)
        .then(res => {
          if (res.data.status != 'ok')
          {
            throw ('fetch error')
          }
  
          let chartData = JSON.parse(JSON.stringify(lineChartData.value))
          chartData.labels = res.data.labels
          chartData.datasets[0].data = res.data.chartData.accumulatedProfitLoss
          lineChartData.value = chartData
        })
        .finally(() => {
          loadingInstance.close()
          loading.value = false
        })
    }

    const fetchPracticeList = async () => {
      await axios.get('/practice-tool/fetch-practice-list')
        .then((res) => {
          practices.value = res.data.practices
        })
    }

    const getDate = (item) => {
      if (item.bodys) {
        const startBody = item.bodys[0]
        const endBody = item.bodys.slice(-1)[0]
        return `${dayjs(startBody.step2_date).format('YYYY-MM-DD')} 〜 ${dayjs(endBody.step3_date).format('YYYY-MM-DD')}`
      }
      return ''
    }

    const getLink = (practiceId) => {
      if (window.location.search) {
        return `/tools/practice-tool/${practiceId}${window.location.search}`
      }
      return `/tools/practice-tool/${practiceId}`
    }

    const geImage = (image) => {
      return image ? require(`@/assets/images/tools/practice/${image}`) : ''
    }

    const linkPractice = (practiceId) => {
      open(getLink(practiceId))
    }

    onMounted(async () => {
      loading.value = true
      const loadingInstance = ElLoading.service()
      await fetchPracticeList()
      await fetchChartData(fundsManagement.value, pickDate.value)
      loadingInstance.close()
      loading.value = false
    })

    watch(pickDate, () => {
      fetchChartData(fundsManagement.value, pickDate.value)
    })

    watch(fundsManagement, () => {
      fetchChartData(fundsManagement.value, pickDate.value)
    })

    return {
      dayjs,
      pageSize,
      optionsDate,
      optionsFunds,
      config,
      getDate,
      getLink,
      geImage,
      pickDate,
      loading,
      fundsManagement,
      pagedList,
      practices,
      lineChartData,
      lineChartOptions,
      apiUrl: process.env.VUE_APP_API_URL,
      linkPractice
    }
  },
})
</script>

<style scoped>
.practice-tool-result {
  padding: 0.5rem 2rem 2rem 2rem;
}
.practice-tool-result .margin-select {
  margin-top: 20px;
  margin-bottom: 5px;
}
.practice-tool-result thead {
  background-color: #c8f3d4;
}
</style>

<style scoped>
/* bootstrap css直書き */
.table {
  width: 100%;
  max-width: 100%;
  margin-bottom: 1rem;
}

.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #eceeef;
}

.table thead th {
  vertical-align: bottom;
  border-bottom: 2px solid #eceeef;
}

.table tbody + tbody {
  border-top: 2px solid #eceeef;
}

.table .table {
  background-color: #fff;
}

.table-sm th,
.table-sm td {
  padding: 0.3rem;
}

.table-borderless th, .table-borderless td {
 border: 0 !important;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.05);
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.075);
}

</style>
